import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './Suporte.css';

const API = '';

const TIPOS = [
  { value: 'erro',      label: 'Erro' },
  { value: 'sugestao',  label: 'Sugestão' },
  { value: 'duvida',    label: 'Dúvida' },
];

const MODULOS = ['STEP→MPR', 'Roteiro', 'FastPrint', 'PLM', 'Geral'];

const STATUS_LABELS = {
  aberto:       'Aberto',
  em_andamento: 'Em andamento',
  resolvido:    'Resolvido',
};

const PRIORIDADE_LABELS = {
  baixa: 'Baixa',
  media: 'Média',
  alta:  'Alta',
};

const FORM_VAZIO = { titulo: '', tipo: 'erro', modulo: 'Geral', descricao: '', imagem: null };

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtData(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR');
}

function getHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

// ── Componente principal ──────────────────────────────────────────────────────

export default function Suporte() {
  const { user } = useAuth();
  const isAdmin = user?.is_superuser;

  const [chamados, setChamados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  // Filtros admin
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroModulo, setFiltroModulo] = useState('');

  // Modal detalhe / edição
  const [modalChamado, setModalChamado] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [editPrioridade, setEditPrioridade] = useState('');
  const [editComentario, setEditComentario] = useState('');
  const [salvandoEdit, setSalvandoEdit] = useState(false);

  // Formulário usuário
  const [form, setForm] = useState(FORM_VAZIO);
  const [preview, setPreview] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [erroForm, setErroForm] = useState(null);
  const [sucessoForm, setSucessoForm] = useState(false);
  const inputImgRef = useRef(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const res = await fetch(`${API}/api/suporte/chamados`, { headers: getHeaders() });
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      setChamados(await res.json());
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // ── Form usuário ────────────────────────────────────────────────────────────

  const handleImagem = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((f) => ({ ...f, imagem: file }));
    setPreview(URL.createObjectURL(file));
  };

  const handleEnviar = async (e) => {
    e.preventDefault();
    setErroForm(null);
    setSucessoForm(false);
    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append('titulo', form.titulo);
      fd.append('tipo', form.tipo);
      fd.append('modulo', form.modulo);
      fd.append('descricao', form.descricao);
      if (form.imagem) fd.append('imagem', form.imagem);

      const res = await fetch(`${API}/api/suporte/chamados`, {
        method: 'POST',
        headers: getHeaders(),
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Erro ${res.status}`);

      setChamados((prev) => [data, ...prev]);
      setForm(FORM_VAZIO);
      setPreview(null);
      if (inputImgRef.current) inputImgRef.current.value = '';
      setSucessoForm(true);
      setTimeout(() => setSucessoForm(false), 4000);
    } catch (e) {
      setErroForm(e.message);
    } finally {
      setEnviando(false);
    }
  };

  // ── Modal admin ─────────────────────────────────────────────────────────────

  const abrirModal = (c) => {
    setModalChamado(c);
    setEditStatus(c.status);
    setEditPrioridade(c.prioridade);
    setEditComentario(c.comentario_admin || '');
  };

  const handleSalvarEdit = async () => {
    setSalvandoEdit(true);
    try {
      const res = await fetch(`${API}/api/suporte/chamados/${modalChamado.id}`, {
        method: 'PUT',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: editStatus,
          prioridade: editPrioridade,
          comentario_admin: editComentario,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Erro ${res.status}`);
      setChamados((prev) => prev.map((c) => (c.id === data.id ? data : c)));
      setModalChamado(data);
    } catch (e) {
      alert(`Erro ao salvar: ${e.message}`);
    } finally {
      setSalvandoEdit(false);
    }
  };

  // ── Filtros ─────────────────────────────────────────────────────────────────

  const chamadosFiltrados = chamados.filter((c) => {
    if (filtroStatus && c.status !== filtroStatus) return false;
    if (filtroModulo && c.modulo !== filtroModulo) return false;
    return true;
  });

  const contadores = {
    aberto:       chamados.filter((c) => c.status === 'aberto').length,
    em_andamento: chamados.filter((c) => c.status === 'em_andamento').length,
    resolvido:    chamados.filter((c) => c.status === 'resolvido').length,
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      <h1 className="cw-page-title">Suporte</h1>
      <div className="suporte-container">
      {erro && <div className="suporte-erro">{erro}</div>}

      {isAdmin ? (
        <AdminView
          chamados={chamadosFiltrados}
          contadores={contadores}
          loading={loading}
          filtroStatus={filtroStatus}
          setFiltroStatus={setFiltroStatus}
          filtroModulo={filtroModulo}
          setFiltroModulo={setFiltroModulo}
          onAbrirModal={abrirModal}
        />
      ) : (
        <UserView
          chamados={chamados}
          loading={loading}
          form={form}
          setForm={setForm}
          preview={preview}
          handleImagem={handleImagem}
          handleEnviar={handleEnviar}
          enviando={enviando}
          erroForm={erroForm}
          sucessoForm={sucessoForm}
          inputImgRef={inputImgRef}
          onAbrirModal={abrirModal}
        />
      )}

      {/* Modal detalhe */}
      {modalChamado && (
        <div className="modal-overlay" onClick={() => setModalChamado(null)}>
          <div className="modal-box modal-suporte" onClick={(e) => e.stopPropagation()}>
            <div className="modal-suporte-header">
              <div>
                <h3>{modalChamado.titulo}</h3>
                <div className="modal-meta">
                  <BadgeStatus status={modalChamado.status} />
                  <BadgePrioridade prioridade={modalChamado.prioridade} />
                  <span className="meta-tipo">{TIPOS.find((t) => t.value === modalChamado.tipo)?.label}</span>
                  <span className="meta-modulo">{modalChamado.modulo}</span>
                </div>
              </div>
              <button className="btn-fechar" onClick={() => setModalChamado(null)}>✕</button>
            </div>

            {isAdmin && modalChamado.usuario && (
              <div className="modal-usuario">
                <strong>Usuário:</strong> {modalChamado.usuario.full_name || modalChamado.usuario.username}{' '}
                <span className="usuario-username">@{modalChamado.usuario.username}</span>
              </div>
            )}

            <p className="modal-data">Aberto em {fmtData(modalChamado.created_at)}</p>

            <div className="modal-descricao">
              <strong>Descrição</strong>
              <p>{modalChamado.descricao}</p>
            </div>

            {modalChamado.imagem_path && (
              <div className="modal-imagem">
                <strong>Imagem anexada</strong>
                <AnexoChamado chamadoId={modalChamado.id} />
              </div>
            )}

            {modalChamado.comentario_admin && !isAdmin && (
              <div className="modal-comentario-admin">
                <strong>Resposta do suporte</strong>
                <p>{modalChamado.comentario_admin}</p>
              </div>
            )}

            {isAdmin && (
              <div className="modal-admin-edit">
                <div className="edit-row">
                  <label>
                    Status
                    <select value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
                      <option value="aberto">Aberto</option>
                      <option value="em_andamento">Em andamento</option>
                      <option value="resolvido">Resolvido</option>
                    </select>
                  </label>
                  <label>
                    Prioridade
                    <select value={editPrioridade} onChange={(e) => setEditPrioridade(e.target.value)}>
                      <option value="baixa">Baixa</option>
                      <option value="media">Média</option>
                      <option value="alta">Alta</option>
                    </select>
                  </label>
                </div>
                <label>
                  Comentário / Resposta
                  <textarea
                    value={editComentario}
                    onChange={(e) => setEditComentario(e.target.value)}
                    rows={4}
                    placeholder="Escreva uma resposta para o usuário..."
                  />
                </label>
                <div className="modal-actions">
                  <button className="btn-outline" onClick={() => setModalChamado(null)}>Fechar</button>
                  <button className="btn-primary" onClick={handleSalvarEdit} disabled={salvandoEdit}>
                    {salvandoEdit ? 'Salvando...' : 'Salvar alterações'}
                  </button>
                </div>
              </div>
            )}

            {!isAdmin && (
              <div className="modal-actions">
                <button className="btn-primary" onClick={() => setModalChamado(null)}>Fechar</button>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </>
  );
}

// ── Subcomponentes ────────────────────────────────────────────────────────────

function AnexoChamado({ chamadoId }) {
  const [src, setSrc] = useState(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let url;
    let cancelado = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/suporte/chamados/${chamadoId}/anexo`, { headers: getHeaders() });
        if (!res.ok) throw new Error(`Erro ${res.status}`);
        const blob = await res.blob();
        url = URL.createObjectURL(blob);
        if (!cancelado) setSrc(url);
      } catch (e) {
        console.error('suporte anexo:', e);
        if (!cancelado) setErro(true);
      }
    })();
    return () => {
      cancelado = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [chamadoId]);

  if (erro) return <p className="suporte-erro">Não foi possível carregar o anexo.</p>;
  if (!src) return <p className="suporte-loading">Carregando anexo...</p>;
  return <img src={src} alt="Anexo do chamado" />;
}

function BadgeStatus({ status }) {
  const cls = { aberto: 'badge-amarelo', em_andamento: 'badge-azul', resolvido: 'badge-verde' }[status] || '';
  return <span className={`badge ${cls}`}>{STATUS_LABELS[status] || status}</span>;
}

function BadgePrioridade({ prioridade }) {
  const cls = { baixa: 'badge-cinza', media: 'badge-laranja', alta: 'badge-vermelho' }[prioridade] || '';
  return <span className={`badge ${cls}`}>{PRIORIDADE_LABELS[prioridade] || prioridade}</span>;
}

function UserView({
  chamados, loading, form, setForm, preview, handleImagem,
  handleEnviar, enviando, erroForm, sucessoForm, inputImgRef, onAbrirModal,
}) {
  return (
    <div className="user-layout">
      {/* Formulário */}
      <div className="card-suporte">
        <h3>Abrir chamado</h3>
        <form onSubmit={handleEnviar}>
          <label>
            Título *
            <input
              type="text"
              required
              value={form.titulo}
              onChange={(e) => setForm({ ...form, titulo: e.target.value })}
              placeholder="Resumo do problema"
            />
          </label>
          <div className="form-row">
            <label>
              Tipo *
              <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                {TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>
            <label>
              Módulo *
              <select value={form.modulo} onChange={(e) => setForm({ ...form, modulo: e.target.value })}>
                {MODULOS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
          </div>
          <label>
            Descrição *
            <textarea
              required
              rows={5}
              value={form.descricao}
              onChange={(e) => setForm({ ...form, descricao: e.target.value })}
              placeholder="Descreva o problema com detalhes..."
            />
          </label>
          <label>
            Imagem (opcional)
            <input type="file" accept="image/*" ref={inputImgRef} onChange={handleImagem} />
          </label>
          {preview && (
            <div className="preview-imagem">
              <img src={preview} alt="Preview" />
            </div>
          )}
          {erroForm && <div className="suporte-erro">{erroForm}</div>}
          {sucessoForm && <div className="suporte-sucesso">Chamado enviado com sucesso!</div>}
          <button type="submit" className="btn-primary" disabled={enviando}>
            {enviando ? 'Enviando...' : 'Enviar chamado'}
          </button>
        </form>
      </div>

      {/* Tabela meus chamados */}
      <div className="card-suporte">
        <h3>Meus chamados</h3>
        {loading ? (
          <p className="suporte-loading">Carregando...</p>
        ) : chamados.length === 0 ? (
          <p className="suporte-vazio">Nenhum chamado aberto ainda.</p>
        ) : (
          <div className="table-wrap">
            <table className="suporte-table">
              <thead>
                <tr>
                  <th>Título</th>
                  <th>Tipo</th>
                  <th>Módulo</th>
                  <th>Status</th>
                  <th>Data</th>
                </tr>
              </thead>
              <tbody>
                {chamados.map((c) => (
                  <tr key={c.id} className="row-clicavel" onClick={() => onAbrirModal(c)}>
                    <td>{c.titulo}</td>
                    <td>{TIPOS.find((t) => t.value === c.tipo)?.label || c.tipo}</td>
                    <td>{c.modulo}</td>
                    <td><BadgeStatus status={c.status} /></td>
                    <td>{fmtData(c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function AdminView({
  chamados, contadores, loading,
  filtroStatus, setFiltroStatus, filtroModulo, setFiltroModulo,
  onAbrirModal,
}) {
  return (
    <div>
      {/* Contadores */}
      <div className="admin-contadores">
        <span className="contador contador-aberto">{contadores.aberto} abertos</span>
        <span className="contador-sep">·</span>
        <span className="contador contador-andamento">{contadores.em_andamento} em andamento</span>
        <span className="contador-sep">·</span>
        <span className="contador contador-resolvido">{contadores.resolvido} resolvidos</span>
      </div>

      {/* Filtros */}
      <div className="admin-filtros">
        <select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
          <option value="">Todos os status</option>
          <option value="aberto">Aberto</option>
          <option value="em_andamento">Em andamento</option>
          <option value="resolvido">Resolvido</option>
        </select>
        <select value={filtroModulo} onChange={(e) => setFiltroModulo(e.target.value)}>
          <option value="">Todos os módulos</option>
          {MODULOS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      {/* Tabela */}
      <div className="card-suporte">
        <h3>Chamados</h3>
        {loading ? (
          <p className="suporte-loading">Carregando...</p>
        ) : chamados.length === 0 ? (
          <p className="suporte-vazio">Nenhum chamado encontrado.</p>
        ) : (
          <div className="table-wrap">
            <table className="suporte-table">
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>Título</th>
                  <th>Tipo</th>
                  <th>Módulo</th>
                  <th>Prioridade</th>
                  <th>Status</th>
                  <th>Data</th>
                </tr>
              </thead>
              <tbody>
                {chamados.map((c) => (
                  <tr key={c.id} className="row-clicavel" onClick={() => onAbrirModal(c)}>
                    <td>{c.usuario?.full_name || c.usuario?.username || '—'}</td>
                    <td>{c.titulo}</td>
                    <td>{TIPOS.find((t) => t.value === c.tipo)?.label || c.tipo}</td>
                    <td>{c.modulo}</td>
                    <td><BadgePrioridade prioridade={c.prioridade} /></td>
                    <td><BadgeStatus status={c.status} /></td>
                    <td>{fmtData(c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
